import {
  AdviceDocumentType,
  AdviceProduct,
  AdviceProductDetailsBackendValues,
  AdviceProductDetailsFormValues,
  AkaCartAdviceValues,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  mapAdviceDataToDetailsFormData,
  mapAdviceDataToIntroFormData,
  mapAdviceIntroFormToIsResident,
  mapDetailsFormDataToAdviceData,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/utils";
import {
  AkaAdvicePriceFormValues,
  AkaAdvisorValues,
} from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/interfaces";
import {
  adviceDetailsFormValuesSelector,
  adviceProductValuesSelector,
} from "Store/advice/stateSelectors";
import {
  CART_RESOURCE_ROOT,
  OPEN_COLLECTIVE_ADVICE_RESOURCE_ROOT,
  OPEN_INDIVIDUAL_ADVICE_RESOURCE_ROOT,
} from "Utils/apiUtils";
import {
  recordAdviceCartIfRecording,
  recordAdviceIfRecording,
  recordAdvicePriceCalculationIfRecording,
} from "Utils/fixtureRecordingUtils";
import { createRequest, getError } from "Utils/requestUtils";
import isEqual from "lodash.isequal";
import omit from "lodash.omit";
import {
  SelectEffect,
  all,
  call,
  put,
  select,
  takeLatest,
} from "redux-saga/effects";
import {
  akaAdviceIntroFormValuesSelector,
  akaAdvicePriceFormValuesSelector,
  akaAdviceResultSelector,
} from "./akaAdviceSelectors";
import {
  AdvicePriceCalculationRequest,
  AdvicePriceCalculationResult,
  OperationType,
} from "./interfaces/AkaAdviceState";
import {
  AdviceResponse,
  CollectiveAdvice,
} from "./interfaces/CollectiveAdvice";
import {
  akaAdvicePriceCalculationRequestStart,
  akaAdvicePriceCalculationRequestSucceeded,
  getAkaAdviceStart,
  getAkaAdviceSucceeded,
  getEditedAkaAdviceStart,
  operationFailed,
} from "./slice";

import { ADVICE_PRICE_STEP } from "Components/Features/CheckPrice/Products/Advice";
import { AdviceProductIntroFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductIntro/interfaces";
import { removeOpenForAdvisors } from "Store/advice/utils";
import { isAdvisorSelector } from "Store/advisor/advisorSelectors";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  updatePriceCalculationFormState,
  updatePriceCalculationFormStates,
} from "Store/priceCalculation/priceCalculationSlice";
import { mapProductToForm } from "Store/priceCalculation/productMappers";
import { ReduxState } from "Store/rootReducer";
import { activateStep } from "Store/steps/stepsSlice";
import { UserIdentificationData } from "Store/userIdentification";
import { userIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { isDefined } from "Utils/functions";
import { AxiosResponse } from "axios";
import { mapToFinalAkaAdviceData } from "./mappers/mapToCartProducts/mapToFinalAkaAdviceData";
import { mapToFormValues } from "./mappers/mapToFormValues/mapToFormValues";
import { mapToPriceRequest } from "./mappers/mapToPriceRequest/mapToPriceRequest";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* getAkaAdvice({
  payload: { adviceProductDetailsBackendValues, isCollectiveAdvice },
}: ReturnType<typeof getAkaAdviceStart>) {
  try {
    const isAdvisor: boolean = yield select(isAdvisorSelector);
    const adviceIntroFormValues: AdviceProductIntroFormValues = yield select(
      akaAdviceIntroFormValuesSelector,
    );
    const adviceDetailsFormValues: AdviceProductDetailsFormValues =
      yield select(adviceDetailsFormValuesSelector);
    const adviceDetailsBackendValues:
      | AdviceProductDetailsBackendValues
      | undefined = yield select(adviceProductValuesSelector);
    const akaAdvicePriceFormValues: AkaAdvicePriceFormValues | undefined =
      yield select(akaAdvicePriceFormValuesSelector);

    const { data } = yield call(createRequest, {
      method: "post",
      requestBody: {
        ...adviceProductDetailsBackendValues,
        isResident: mapAdviceIntroFormToIsResident(adviceIntroFormValues),
      },
      url: isCollectiveAdvice
        ? OPEN_COLLECTIVE_ADVICE_RESOURCE_ROOT
        : OPEN_INDIVIDUAL_ADVICE_RESOURCE_ROOT,
      open: false,
    });
    recordAdviceIfRecording(data);

    const { payload: adviceResponse } = data as AdviceResponse;

    yield put(getAkaAdviceSucceeded(adviceResponse));

    const formState = mapToFormValues(
      isCollectiveAdvice,
      isAdvisor,
      adviceResponse,
      akaAdvicePriceFormValues,
    );

    const didAdviceDetailsFormValuesChange =
      !isDefined(adviceDetailsBackendValues) ||
      formValuesDiff(
        isAdvisor,
        adviceDetailsBackendValues,
        adviceDetailsFormValues,
      );
    if (didAdviceDetailsFormValuesChange) {
      yield put(
        updatePriceCalculationFormState({
          stepId: ADVICE_PRICE_STEP.id,
          formState,
        }),
      );
      yield put(
        akaAdvicePriceCalculationRequestStart({
          isCollectiveAdvice,
          priceFormValues: formState,
        }),
      );
    } else {
      if (!isDefined(akaAdvicePriceFormValues)) {
        throw Error("akaAdvicePriceFormValues is not defined");
      }
      yield put(
        akaAdvicePriceCalculationRequestStart({
          isCollectiveAdvice,
          priceFormValues: akaAdvicePriceFormValues,
        }),
      );
    }
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.SEND_GET_ADVICE_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* getEditedAkaAdvice({
  payload: { isCollectiveAdvice, cart },
}: ReturnType<typeof getEditedAkaAdviceStart>) {
  try {
    const {
      newCustomerResponse: { dateOfBirth },
    }: UserIdentificationData = yield select(userIdentificationSelector);

    const advice = cart.advice;
    if (!isDefined(advice)) {
      throw Error("Advice is not on cart!");
    }

    const adviceIntroFormValues = mapAdviceDataToIntroFormData(
      advice.adviceProductValues.isStayingInNorwayForMoreThanFiveYears,
    );
    const adviceDetailsFormValues = mapAdviceDataToDetailsFormData(
      advice.adviceProductValues,
    );
    const { data }: AxiosResponse<AdviceResponse> = yield call(createRequest, {
      method: "post",
      requestBody: {
        ...mapDetailsFormDataToAdviceData({
          ...adviceDetailsFormValues,
          dateOfBirth,
        }),
        isResident: mapAdviceIntroFormToIsResident(adviceIntroFormValues),
      },
      url: isCollectiveAdvice
        ? OPEN_COLLECTIVE_ADVICE_RESOURCE_ROOT
        : OPEN_INDIVIDUAL_ADVICE_RESOURCE_ROOT,
      open: false,
    });
    const { payload: collectiveAdvice } = data;

    const isAdvisor: boolean = yield select(isAdvisorSelector);
    yield put(
      updatePriceCalculationFormStates(
        mapProductToForm(
          INSURANCE_NAME_VALUE.ADVICE,
          cart,
          isAdvisor,
          collectiveAdvice,
        ),
      ),
    );

    recordAdviceIfRecording(data);
    yield put(getAkaAdviceSucceeded(collectiveAdvice));
    const akaAdvicePriceFormValues: AkaAdvicePriceFormValues | undefined =
      yield select(akaAdvicePriceFormValuesSelector);
    if (!isDefined(akaAdvicePriceFormValues)) {
      throw Error("akaAdvicePriceFormValues is not defined");
    }
    yield put(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice,
        priceFormValues: akaAdvicePriceFormValues,
      }),
    );
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.SEND_GET_ADVICE_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* akaAdvicePriceCalculationRequest({
  payload: { isCollectiveAdvice, priceFormValues, specificProduct },
}: ReturnType<typeof akaAdvicePriceCalculationRequestStart>) {
  try {
    const adviceDetailsFormValues: AdviceProductDetailsFormValues =
      yield select(adviceDetailsFormValuesSelector);
    const isAdvisor: boolean = yield select(isAdvisorSelector);
    const {
      discountResponse,
      newCartResponse,
      newCustomerResponse: { customerRef, dateOfBirth, isFemale },
    }: UserIdentificationData = yield select(userIdentificationSelector);
    const collectiveAdvice: CollectiveAdvice | null = yield select(
      akaAdviceResultSelector,
    );

    if (!isDefined(collectiveAdvice)) {
      throw Error("collectiveAdvice is not defined");
    }

    const advicePriceCalculationRequests: AdvicePriceCalculationRequest[] =
      mapToPriceRequest(
        isCollectiveAdvice,
        isAdvisor,
        collectiveAdvice,
        priceFormValues,
        adviceDetailsFormValues,
        dateOfBirth ?? "",
        specificProduct,
      );

    const responses: any[] = yield all(
      advicePriceCalculationRequests.map((advicePriceCalculationRequest) => {
        return call(createRequest, {
          method: "post",
          requestBody: {
            discount: discountResponse.f2100,
            r: {
              cartId: newCartResponse.cartId,
              customerRef,
              dateOfBirth,
              isFemale,
              isSmoker: false,
              ...advicePriceCalculationRequest.data,
            },
          },
          url: removeOpenForAdvisors(
            advicePriceCalculationRequest.productPriceUrl,
            isAdvisor,
          ),
          open: !isAdvisor,
        });
      }),
    );
    const priceCalculationResults: AdvicePriceCalculationResult[] =
      responses.map((res) => {
        recordAdvicePriceCalculationIfRecording(res.config.url, res.data);
        const data = res.data.configurations;
        const configurationName: string =
          Object.keys(data).find((key) => key) ?? "";
        return {
          configurationName,
          composites: res.data.composites,
          ...data[configurationName],
        };
      });

    yield put(
      akaAdvicePriceCalculationRequestSucceeded(priceCalculationResults),
    );
    yield put(activateStep(ADVICE_PRICE_STEP.id));
  } catch (e) {
    // TODO - If one product-price-request fails the whole operation fails. Shouldn't this be handled in a better way?
    yield put(
      operationFailed({
        operationType: OperationType.SEND_ADVICE_PRICE_CALCULATION_REQUEST,
        response: getError(e),
      }),
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* handleCartAkaAdviceUpdateRequest(
  priceFormData: AkaAdvicePriceFormValues,
) {
  const isAdvisor: boolean = yield select(isAdvisorSelector);
  const userIdentificationData: UserIdentificationData = yield select(
    userIdentificationSelector,
  );
  const adviceDetailsStepFormValues: AdviceProductDetailsFormValues =
    yield select(adviceDetailsFormValuesSelector);
  const adviceProducts: AdviceProduct[] = yield select(
    (state: ReduxState): AdviceProduct[] | undefined =>
      state.cart.current?.advice?.adviceProducts,
  );
  const adviceDocumentId: string | undefined = yield select(
    (state: ReduxState): string | undefined =>
      state.advicePDF.response.current?.payload.id,
  );
  const advisorValues: AkaAdvisorValues = yield call(getAdvisorValues);

  const adviceUpdatePayload = getAdviceUpdatePayloadWithPrices(
    mapDetailsFormDataToAdviceData(adviceDetailsStepFormValues),
    priceFormData,
    isAdvisor,
    advisorValues,
    adviceDocumentId,
  );

  const { data } = yield call(createRequest, {
    method: "post",
    requestBody: { ...adviceUpdatePayload, adviceProducts },
    url: `${CART_RESOURCE_ROOT}/${userIdentificationData?.newCartResponse?.cartId}/products/advice`,
    open: false,
  });

  recordAdviceCartIfRecording(data);
}

function* getAdvisorValues(): Generator<
  SelectEffect,
  AkaAdvisorValues,
  AdviceProductDetailsFormValues & AkaAdvicePriceFormValues
> {
  const { situationComment }: AdviceProductDetailsFormValues = yield select(
    adviceDetailsFormValuesSelector,
  );
  const advicePriceFormValues: AkaAdvicePriceFormValues = yield select(
    akaAdvicePriceFormValuesSelector,
  );
  return {
    situationComment,
    deathAdviceComment: advicePriceFormValues.deathAdviceComment,
    criticalIllnessAdviceComment:
      advicePriceFormValues.criticalIllnessAdviceComment,
    disabilityAdviceComment: advicePriceFormValues.disabilityAdviceComment,
    disabilityPaymentAfter3Months:
      advicePriceFormValues.disabilityPaymentAfter3Months,
    disabilityOneTimePaymentAdvisorChoice:
      advicePriceFormValues.disabilityOneTimePaymentAdvisorChoice,
    disabilityOneTimePaymentAdvisorValue:
      advicePriceFormValues.disabilityOneTimePaymentAdvisorValue,
  };
}

export function* akaAdviceSaga(): Generator {
  yield takeLatest(getAkaAdviceStart, getAkaAdvice);
  yield takeLatest(getEditedAkaAdviceStart, getEditedAkaAdvice);
  yield takeLatest(
    akaAdvicePriceCalculationRequestStart,
    akaAdvicePriceCalculationRequest,
  );
}

const getAdviceUpdatePayloadWithPrices = (
  adviceDetailsStepFormValues: AdviceProductDetailsBackendValues,
  advicePriceStepFormValues: AkaAdvicePriceFormValues,
  isAdvisor: boolean,
  advisorValues: AkaAdvisorValues,
  adviceDocumentId?: string,
): AkaCartAdviceValues => {
  const adviceUpdatePayload: AkaCartAdviceValues = {
    adviceProductValues: adviceDetailsStepFormValues,
    finalAkaAdviceData: mapToFinalAkaAdviceData(advicePriceStepFormValues),
  };
  if (isDefined(adviceDocumentId)) {
    adviceUpdatePayload.adviceDocumentId = adviceDocumentId;
    adviceUpdatePayload.adviceDocumentType = isAdvisor
      ? AdviceDocumentType.ADVICE_RECOMMENDED
      : AdviceDocumentType.ADVICE_CHOSEN;
  }
  if (isAdvisor) {
    adviceUpdatePayload.adviceProductValues = {
      ...adviceUpdatePayload.adviceProductValues,
      isAdvisor,
      advisorValues: adviceUpdatePayload.adviceProductValues.advisorValues,
    };
  }
  return adviceUpdatePayload;
};

const formValuesDiff = (
  isAdvisor: boolean,
  adviceProductValues?: AdviceProductDetailsBackendValues,
  adviceFormValues?: AdviceProductDetailsFormValues,
): boolean => {
  if (!isDefined(adviceFormValues)) {
    return false;
  }
  const mappedAdviceFormValues = mapDetailsFormDataToAdviceData(
    adviceFormValues,
    isAdvisor,
  );
  if (isAdvisor && adviceProductValues) {
    const properties = ["isAdvisor", "advisorValues", "situationComment"];
    const backendValues = omit(adviceProductValues, properties);
    const formValues = omit(mappedAdviceFormValues, properties);
    return !isEqual(backendValues, formValues);
  }
  return !isEqual(adviceProductValues, mappedAdviceFormValues);
};
